import { useEffect, useMemo, useState } from "react";
import {
  civilToInstant,
  instantToCivil,
  parseEventText,
  type CalendarEvent,
  type CalendarSystem,
  type Category,
  type CivilDate,
  type EventInput,
  type EventStatus,
  type Lang,
} from "@calendar/shared";
import { n, reminderText } from "../format.js";
import { api } from "../api.js";
import { MeetingRecords } from './MeetingRecords.js';

interface Props {
  event: CalendarEvent | "new";
  seed: { startUtc: string; day: CivilDate | null } | null;
  timeZone: string;
  calendar: CalendarSystem;
  lang: Lang;
  categories: Category[];
  t: (key: string, vars?: Record<string, string | number>) => string;
  onClose: () => void;
  onSave: (input: EventInput, id?: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onAttach: (eventId: string, file: File) => Promise<void>;
  onSetReminder: (eventId: string, minutes: number) => Promise<void>;
  toast: (text: string, kind?: "ok" | "error") => void;
}

const STATUSES: EventStatus[] = ["confirmed", "tentative", "cancelled", "done"];
const REMINDERS = [0, 5, 15, 30, 60, 1440];

interface FormState {
  title: string;
  date: string;
  start: string;
  end: string;
  allDay: boolean;
  location: string;
  notes: string;
  categoryId: string;
  status: EventStatus;
  reminderMinutes: number;
}

function toForm(event: CalendarEvent | "new", seed: Props["seed"], timeZone: string, calendar: CalendarSystem): FormState {
  const iso = event === "new" ? seed?.startUtc ?? new Date().toISOString() : event.startUtc;
  const startCivil = instantToCivil(iso, timeZone, calendar);
  const endIso = event === "new" ? new Date(new Date(iso).getTime() + 3600_000).toISOString() : event.endUtc;
  const endCivil = instantToCivil(endIso, timeZone, calendar);
  return {
    title: event === "new" ? "" : event.title,
    date: `${startCivil.date.year}-${String(startCivil.date.month).padStart(2, "0")}-${String(startCivil.date.day).padStart(2, "0")}`,
    start: `${String(startCivil.hour).padStart(2, "0")}:${String(startCivil.minute).padStart(2, "0")}`,
    end: `${String(endCivil.hour).padStart(2, "0")}:${String(endCivil.minute).padStart(2, "0")}`,
    allDay: event === "new" ? false : event.allDay,
    location: event === "new" ? "" : event.location,
    notes: event === "new" ? "" : event.notes,
    categoryId: event === "new" ? "" : event.categoryId ?? "",
    status: event === "new" ? "confirmed" : event.status,
    reminderMinutes: event === "new" ? 0 : event.reminderMinutes,
  };
}

const toMinutes = (hhmm: string): number => {
  const [h, m] = hhmm.split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
};

const fromMinutes = (mins: number): string =>
  `${String(Math.floor(mins / 60) % 24).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;

export function EventEditor(props: Props) {
  const { event, seed, timeZone, calendar, lang, categories, t, onClose, onSave, onDelete, onAttach, onSetReminder, toast } = props;
  const [form, setForm] = useState<FormState>(() => toForm(event, seed, timeZone, calendar));
  const [busy, setBusy] = useState(false);
  const [attachments, setAttachments] = useState<CalendarEvent["attachments"]>(event === "new" ? [] : event.attachments);

  useEffect(() => {
    setForm(toForm(event, seed, timeZone, calendar));
    setAttachments(event === "new" ? [] : event.attachments);
  }, [event, seed, timeZone, calendar]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const parsedDate = useMemo(() => {
    const [y, m, d] = form.date.split("-").map(Number);
    if (!y || !m || !d) return null;
    return { year: y, month: m, day: d };
  }, [form.date]);

  // Convert the wall-clock fields back into a UTC instant using the user's
  // zone and calendar, so a 10:00 Tehran meeting stays 10:00 after DST shifts.
  const toUtc = (hhmm: string): string | null => {
    if (!parsedDate) return null;
    return civilToInstant(parsedDate, Math.floor(toMinutes(hhmm) / 60), toMinutes(hhmm) % 60, timeZone, calendar);
  };

  const valid = form.title.trim().length >= 1 && parsedDate !== null;

  async function save() {
    if (!valid) return;
    setBusy(true);
    try {
      const startUtc = toUtc(form.allDay ? "00:00" : form.start);
      if (!startUtc) {
        toast(t("error.generic"), "error");
        return;
      }
      let endUtc = form.allDay ? null : toUtc(form.end);
      // A missing or backwards end time means a one-hour meeting.
      if (!endUtc || endUtc <= startUtc) {
        endUtc = new Date(new Date(startUtc).getTime() + 3600_000).toISOString();
      }
      await onSave(
        {
          title: form.title.trim(),
          startUtc,
          endUtc,
          allDay: form.allDay,
          timeZone,
          location: form.location.trim(),
          notes: form.notes,
          categoryId: form.categoryId || null,
          status: form.status,
          reminderMinutes: form.reminderMinutes,
        },
        event === "new" ? undefined : event.id,
      );
    } finally {
      setBusy(false);
    }
  }

  /** Fill the form from a natural-language sentence. */
  function applyQuickAdd(text: string) {
    const parsed = parseEventText(text, { timeZone, calendar });
    if (!parsed.startUtc) {
      toast(t("quickAddNoDate"), "error");
      return;
    }
    const s = instantToCivil(parsed.startUtc, timeZone, calendar);
    const e = instantToCivil(parsed.endUtc, timeZone, calendar);
    setForm((f) => ({
      ...f,
      title: parsed.title || f.title,
      date: `${s.date.year}-${String(s.date.month).padStart(2, "0")}-${String(s.date.day).padStart(2, "0")}`,
      start: fromMinutes(s.hour * 60 + s.minute),
      end: fromMinutes(e.hour * 60 + e.minute),
      allDay: parsed.allDay,
    }));
  }

  return (
    <div className="overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="dialog" role="dialog" aria-modal="true">
        <h2 className="dialog__title">
          {event === "new" ? t("action.new") : t("action.edit")}
        </h2>

        {event === "new" && (
          <div className="quickadd">
            <QuickAdd t={t} onApply={applyQuickAdd} />
          </div>
        )}

        <div className="field">
          <label htmlFor="f-title">{t("field.title")}</label>
          <input
            id="f-title"
            className="input"
            value={form.title}
            placeholder={t("field.titlePlaceholder")}
            onChange={(e) => set("title", e.target.value)}
            autoFocus
          />
        </div>

        <div className="row">
          <div className="field">
            <label htmlFor="f-date">{t("field.date")}</label>
            <input
              id="f-date"
              className="input"
              type="date"
              value={form.date}
              onChange={(e) => set("date", e.target.value)}
            />
          </div>
          {!form.allDay && (
            <>
              <div className="field">
                <label htmlFor="f-start">{t("field.start")}</label>
                <input
                  id="f-start"
                  className="input"
                  type="time"
                  value={form.start}
                  onChange={(e) => set("start", e.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor="f-end">{t("field.end")}</label>
                <input
                  id="f-end"
                  className="input"
                  type="time"
                  value={form.end}
                  onChange={(e) => set("end", e.target.value)}
                />
              </div>
            </>
          )}
        </div>

        <div className="field field--row">
          <input
            id="f-allday"
            type="checkbox"
            checked={form.allDay}
            onChange={(e) => set("allDay", e.target.checked)}
          />
          <label htmlFor="f-allday">{t("field.allDay")}</label>
        </div>

        <div className="field">
          <label htmlFor="f-location">{t("field.location")}</label>
          <input
            id="f-location"
            className="input"
            value={form.location}
            onChange={(e) => set("location", e.target.value)}
          />
        </div>

        <div className="row">
          {categories.length > 0 && (
            <div className="field">
              <label htmlFor="f-category">{t("field.category")}</label>
              <select
                id="f-category"
                className="select"
                value={form.categoryId}
                onChange={(e) => set("categoryId", e.target.value)}
              >
                <option value="">{t("field.none")}</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="field">
            <label htmlFor="f-status">{t("field.status")}</label>
            <select
              id="f-status"
              className="select"
              value={form.status}
              onChange={(e) => set("status", e.target.value as EventStatus)}
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {t("status." + s)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="field">
          <span className="muted">{t("field.reminder")}</span>
          <div className="chips">
            {REMINDERS.map((m) => (
              <button
                key={m}
                type="button"
                className="chipbtn"
                aria-pressed={form.reminderMinutes === m}
                onClick={() => {
                  set("reminderMinutes", m);
                  if (event !== "new") void onSetReminder(event.id, m);
                }}
              >
                {reminderText(m, t, (x) => n(x, lang))}
              </button>
            ))}
          </div>
        </div>

        <div className="field">
          <label htmlFor="f-notes">{t("field.notes")}</label>
          <textarea
            id="f-notes"
            className="textarea"
            value={form.notes}
            placeholder={t("field.notesPlaceholder")}
            onChange={(e) => set("notes", e.target.value)}
          />
        </div>

        {event !== "new" && (
          <div className="field">
            <span className="muted">{t("action.attach")}</span>
            <AttachmentRow
              eventId={event.id}
              attachments={attachments ?? []}
              lang={lang}
              t={t}
              onAttach={onAttach}
              onChange={setAttachments}
            />
          </div>
        )}

        {event !== 'new' && <MeetingRecords key={event.id} eventId={event.id} lang={lang} />}
        <div className="dialog__actions">
          {event !== "new" && (
            <button
              type="button"
              className="btn btn--danger"
              onClick={() => void onDelete(event.id)}
              style={{ marginInlineEnd: "auto" }}
            >
              {t("action.delete")}
            </button>
          )}
          <button type="button" className="btn" onClick={onClose}>
            {t("action.cancel")}
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={!valid || busy}
            onClick={() => void save()}
          >
            {t("action.save")}
          </button>
        </div>
      </div>
    </div>
  );
}

function QuickAdd({ t, onApply }: { t: Props["t"]; onApply: (text: string) => void }) {
  const [text, setText] = useState("");
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (text.trim()) onApply(text.trim());
      }}
    >
      <div className="field">
        <label htmlFor="f-quick">{t("quickAdd")}</label>
        <input
          id="f-quick"
          className="input"
          value={text}
          placeholder={t("quickAddPlaceholder")}
          onChange={(e) => setText(e.target.value)}
        />
        <div className="quickadd__hint">{t("quickAddHint")}</div>
      </div>
    </form>
  );
}

function AttachmentRow({
  eventId,
  attachments,
  lang,
  t,
  onAttach,
  onChange,
}: {
  eventId: string;
  attachments: NonNullable<CalendarEvent["attachments"]>;
  lang: Lang;
  t: Props["t"];
  onAttach: (id: string, file: File) => Promise<void>;
  onChange: (next: NonNullable<CalendarEvent["attachments"]>) => void;
}) {
  return (
    <div className="attachments">
      {attachments.map((a) => (
        <div className="attachment" key={a.id}>
          <AttachmentBody attachment={a} lang={lang} />
          <button
            type="button"
            className="icon-btn"
            style={{ marginInlineStart: "auto" }}
            onClick={async () => {
              await api.deleteAttachment(a.id);
              onChange(attachments.filter((x) => x.id !== a.id));
            }}
          >
            ✕
          </button>
        </div>
      ))}
      <label className="attachments__drop">
        {t("action.attach")}
        <input
          type="file"
          hidden
          onChange={async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            await onAttach(eventId, file);
            const fresh = await api.getEvent(eventId);
            onChange(fresh.attachments ?? []);
          }}
        />
      </label>
    </div>
  );
}

/**
 * One attachment, whose bytes are private: the URL needs auth headers, so it is
 * fetched into a blob and shown as an object URL. The URL is revoked when the
 * component unmounts so images do not leak for the life of the tab.
 */
function AttachmentBody({ attachment, lang }: { attachment: NonNullable<CalendarEvent["attachments"]>[number]; lang: Lang }) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let url: string | null = null;
    let cancelled = false;
    void (async () => {
      try {
        const blob = await api.fetchAttachment(attachment.url);
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setObjectUrl(url);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [attachment.url]);

  function open() {
    if (!objectUrl) return;
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = attachment.fileName;
    a.click();
  }

  return (
    <>
      {attachment.kind === "image" && objectUrl ? (
        <img src={objectUrl} alt={attachment.fileName} />
      ) : (
        <span aria-hidden="true">{failed ? "⚠" : "📄"}</span>
      )}
      <button
        type="button"
        className="btn"
        style={{ padding: "2px 8px", border: 0, background: "transparent", color: "var(--accent)" }}
        onClick={open}
        disabled={!objectUrl}
      >
        {attachment.fileName}
      </button>
      <span className="muted">{n(Math.round(attachment.sizeBytes / 1024), lang)} KB</span>
    </>
  );
}
